import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

function thumbnailStatusLabel(item) {
  if (item.has_thumbnail) return null;
  if (item.thumbnail_status === 'failed') {
    return item.thumbnail_error || 'Thumbnail failed';
  }
  if (item.thumbnail_status === 'processing') return 'Generating thumbnail…';
  return 'Thumbnail pending';
}

function CaptureTile({ item, fetchDownloadUrl, onOpen }) {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [thumbError, setThumbError] = useState(null);
  const [loadingThumb, setLoadingThumb] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoadingThumb(true);
      setThumbError(null);
      try {
        const variant = item.has_thumbnail ? 'thumbnail' : 'original';
        const url = await fetchDownloadUrl(item.id, variant);
        if (!cancelled) setThumbUrl(url);
      } catch (err) {
        if (!cancelled) {
          if (item.has_thumbnail) {
            try {
              const originalUrl = await fetchDownloadUrl(item.id, 'original');
              if (!cancelled) setThumbUrl(originalUrl);
              return;
            } catch {
              // fall through to error state
            }
          }
          setThumbError(err.message || 'Preview unavailable');
          setThumbUrl(null);
        }
      } finally {
        if (!cancelled) setLoadingThumb(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [item.id, item.has_thumbnail, fetchDownloadUrl]);

  const statusNote = thumbnailStatusLabel(item);

  return (
    <button
      type="button"
      className="btn btn-link p-0 border rounded overflow-hidden text-start capture-media-tile"
      style={{ width: 140 }}
      onClick={() => onOpen(item.id)}
      title={item.label || item.filename}
    >
      <div
        className="bg-light d-flex align-items-center justify-content-center"
        style={{ width: 140, height: 140 }}
      >
        {loadingThumb && (
          <span className="spinner-border spinner-border-sm text-muted" role="status" />
        )}
        {!loadingThumb && thumbUrl && (
          <img
            src={thumbUrl}
            alt={item.label || item.filename}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        {!loadingThumb && !thumbUrl && (
          <i className="bi bi-image text-muted" style={{ fontSize: '2rem' }} aria-hidden />
        )}
      </div>
      <div className="p-2 small">
        <div className="fw-semibold text-truncate">{item.label || item.filename}</div>
        {item.workflow_step_id && (
          <div className="text-muted text-truncate">{item.workflow_step_id}</div>
        )}
        {(statusNote || thumbError) && (
          <div className="text-warning-emphasis text-truncate" title={statusNote || thumbError}>
            {statusNote || thumbError}
          </div>
        )}
      </div>
    </button>
  );
}

CaptureTile.propTypes = {
  item: PropTypes.object.isRequired,
  fetchDownloadUrl: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
};

export default function CaptureMediaGrid({ items, loading, error, fetchDownloadUrl, onOpen }) {
  if (loading) {
    return (
      <div className="text-muted small">
        <span className="spinner-border spinner-border-sm me-2" role="status" />
        Loading photos…
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger py-2 small mb-0">{error}</div>;
  }

  if (!items.length) {
    return <div className="text-muted small">No field photos yet.</div>;
  }

  return (
    <div className="d-flex flex-wrap gap-3">
      {items.map((item) => (
        <CaptureTile
          key={item.id}
          item={item}
          fetchDownloadUrl={fetchDownloadUrl}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
}

CaptureMediaGrid.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  fetchDownloadUrl: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
};

CaptureMediaGrid.defaultProps = {
  loading: false,
  error: null,
};
